import { useState, useEffect, useRef } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  ReorderButtons,
} from "../../../components/FormField";
import { moveItem } from "./shared";

const SOCIAL_ICON_OPTIONS = [
  "Linkedin",
  "Facebook",
  "Instagram",
  "MessageCircle",
  "Twitter",
  "Youtube",
  "Globe",
];

export function Footer({ initialData, onChange }) {
  /* ---------------- STATE ---------------- */
  const [companyDesc, setCompanyDesc] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneHref, setPhoneHref] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [addressHref, setAddressHref] = useState("");

  const [footerSections, setFooterSections] = useState([
    { title: "", links: [{ name: "", url: "" }] },
  ]);

  const [copyright, setCopyright] = useState("");
  const [bottomLinks, setBottomLinks] = useState([{ name: "", url: "" }]);
  const [socialLinks, setSocialLinks] = useState([
    { platform: "", icon: "", url: "" },
  ]);

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.company_info) {
      setCompanyDesc(d.company_info.description || "");
      setPhone(d.company_info.phone || "");
      setPhoneHref(d.company_info.phone_href || "");
      setEmail(d.company_info.email || "");
      setAddress(d.company_info.address || "");
      setAddressHref(d.company_info.address_href || "");
    }
    if (d.footer_sections?.length) setFooterSections(d.footer_sections);
    if (d.bottom_bar) {
      setCopyright(d.bottom_bar.copyright || "");
      if (d.bottom_bar.links?.length) setBottomLinks(d.bottom_bar.links);
      if (d.bottom_bar.social_links?.length)
        setSocialLinks(d.bottom_bar.social_links);
    }
  }, [initialData]);

  /* ---------------- SYNC TO PARENT (debounced) ---------------- */
  const ref = useRef({});
  ref.current = {
    companyDesc,
    phone,
    phoneHref,
    email,
    address,
    addressHref,
    footerSections,
    copyright,
    bottomLinks,
    socialLinks,
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const s = ref.current;
      onChange?.({
        company_info: {
          description: s.companyDesc,
          phone: s.phone,
          phone_href: s.phoneHref,
          email: s.email,
          address: s.address,
          address_href: s.addressHref,
        },
        footer_sections: s.footerSections,
        bottom_bar: {
          copyright: s.copyright,
          links: s.bottomLinks,
          social_links: s.socialLinks,
        },
      });
    }, 400);
    return () => clearTimeout(t);
  }, [
    companyDesc,
    phone,
    phoneHref,
    email,
    address,
    addressHref,
    footerSections,
    copyright,
    bottomLinks,
    socialLinks,
  ]);

  /* ---------------- FOOTER SECTIONS HELPERS ---------------- */
  const updateSectionTitle = (si, val) =>
    setFooterSections((s) =>
      s.map((sec, i) => (i === si ? { ...sec, title: val } : sec)),
    );

  const addSection = () =>
    setFooterSections((s) => [
      ...s,
      { title: "", links: [{ name: "", url: "" }] },
    ]);

  const removeSection = (si) =>
    setFooterSections((s) => s.filter((_, i) => i !== si));

  const addLink = (si) =>
    setFooterSections((s) =>
      s.map((sec, i) =>
        i === si
          ? { ...sec, links: [...sec.links, { name: "", url: "" }] }
          : sec,
      ),
    );

  const removeLink = (si, li) =>
    setFooterSections((s) =>
      s.map((sec, i) =>
        i === si
          ? { ...sec, links: sec.links.filter((_, idx) => idx !== li) }
          : sec,
      ),
    );

  const updateLink = (si, li, key, val) =>
    setFooterSections((s) =>
      s.map((sec, i) =>
        i === si
          ? {
              ...sec,
              links: sec.links.map((lk, idx) =>
                idx === li ? { ...lk, [key]: val } : lk,
              ),
            }
          : sec,
      ),
    );

  const moveSectionLink = (si, from, to) =>
    setFooterSections((s) =>
      s.map((sec, i) =>
        i === si ? { ...sec, links: moveItem(sec.links, from, to) } : sec,
      ),
    );

  /* ---------------- BOTTOM LINKS HELPERS ---------------- */
  const updateBottomLink = (i, key, val) =>
    setBottomLinks((s) =>
      s.map((lk, idx) => (idx === i ? { ...lk, [key]: val } : lk)),
    );

  /* ---------------- SOCIAL LINKS HELPERS ---------------- */
  const updateSocialLink = (i, key, val) =>
    setSocialLinks((s) =>
      s.map((lk, idx) => (idx === i ? { ...lk, [key]: val } : lk)),
    );

  /* ---------------- RENDER ---------------- */
  return (
    <div className="max-w-7xl mx-auto w-full space-y-4 py-6">
      {/* COMPANY INFO */}
      <CollapsibleSection
        title="Company Info"
        active={true}
        onToggleActive={() => {}}
      >
        <Field label="Description">
          <TextArea
            value={companyDesc}
            onChange={(e) => setCompanyDesc(e.target.value)}
            placeholder="Australia's trusted SAP Silver & Salesforce Partner…"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone">
            <TextInput
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+61 3 7075 3900"
            />
          </Field>
          <Field label="Phone href (tel:...)">
            <TextInput
              value={phoneHref}
              onChange={(e) => setPhoneHref(e.target.value)}
              placeholder="tel:0370753900"
            />
          </Field>
        </div>
        <Field label="Email">
          <TextInput
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="info@thynkprocess.com.au"
          />
        </Field>
        <Field label="Address">
          <TextInput
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="39/72 York Street, South Melbourne Victoria 3205"
          />
        </Field>
        <Field label="Google Maps URL">
          <TextInput
            value={addressHref}
            onChange={(e) => setAddressHref(e.target.value)}
            placeholder="https://maps.app.goo.gl/..."
          />
        </Field>
      </CollapsibleSection>

      {/* FOOTER LINK COLUMNS */}
      <CollapsibleSection
        title="Footer Sections"
        active={true}
        onToggleActive={() => {}}
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Columns{" "}
            <span className="normal-case font-normal">
              ({footerSections.length}/4)
            </span>
          </p>
          {footerSections.length < 4 && (
            <button
              type="button"
              onClick={addSection}
              className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
              style={{
                background: "hsl(var(--accent-muted))",
                color: "hsl(var(--accent))",
              }}
            >
              <Plus className="w-3.5 h-3.5" /> Add Column
            </button>
          )}
        </div>

        {footerSections.map((sec, si) => (
          <div
            key={si}
            className="p-3 rounded-lg border border-border space-y-3 mb-3"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted-foreground">
                Column {si + 1}
              </p>
              <div className="flex items-center gap-2">
                <ReorderButtons
                  index={si}
                  total={footerSections.length}
                  onMove={(f, t) =>
                    setFooterSections(moveItem(footerSections, f, t))
                  }
                />
                {footerSections.length > 1 && (
                  <button
                    onClick={() => removeSection(si)}
                    className="text-destructive"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={sec.title}
                onChange={(e) => updateSectionTitle(si, e.target.value)}
                placeholder="e.g. Company"
              />
            </Field>

            <div className="pt-3 border-t border-border">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Links{" "}
                  <span className="normal-case font-normal">
                    ({sec.links.length}/5)
                  </span>
                </p>
                {sec.links.length < 5 && (
                  <button
                    type="button"
                    onClick={() => addLink(si)}
                    className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
                    style={{
                      background: "hsl(var(--accent-muted))",
                      color: "hsl(var(--accent))",
                    }}
                  >
                    <Plus className="w-3.5 h-3.5" /> Add
                  </button>
                )}
              </div>
              {sec.links.map((lk, li) => (
                <div key={li} className="flex gap-2 mb-2">
                  <TextInput
                    value={lk.name}
                    onChange={(e) => updateLink(si, li, "name", e.target.value)}
                    placeholder="Label"
                    className="flex-1"
                  />
                  <TextInput
                    value={lk.url}
                    onChange={(e) => updateLink(si, li, "url", e.target.value)}
                    placeholder="/path"
                    className="flex-1"
                  />
                  <ReorderButtons
                    index={li}
                    total={sec.links.length}
                    onMove={(f, t) => moveSectionLink(si, f, t)}
                  />
                  {sec.links.length > 1 && (
                    <button
                      onClick={() => removeLink(si, li)}
                      className="text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* BOTTOM BAR */}
      <CollapsibleSection
        title="Bottom Bar"
        active={true}
        onToggleActive={() => {}}
      >
        <Field label="Copyright Text">
          <TextInput
            value={copyright}
            onChange={(e) => setCopyright(e.target.value)}
            placeholder="© 2026 ThynkProcess Pty Ltd. All rights reserved."
          />
        </Field>

        {/* Bottom Links */}
        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Bottom Links
            </p>
            <button
              type="button"
              onClick={() =>
                setBottomLinks((s) => [...s, { name: "", url: "" }])
              }
              className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
              style={{
                background: "hsl(var(--accent-muted))",
                color: "hsl(var(--accent))",
              }}
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          {bottomLinks.map((lk, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <TextInput
                value={lk.name}
                onChange={(e) => updateBottomLink(i, "name", e.target.value)}
                placeholder="Label"
                className="flex-1"
              />
              <TextInput
                value={lk.url}
                onChange={(e) => updateBottomLink(i, "url", e.target.value)}
                placeholder="/path"
                className="flex-1"
              />
              <ReorderButtons
                index={i}
                total={bottomLinks.length}
                onMove={(f, t) => setBottomLinks(moveItem(bottomLinks, f, t))}
              />
              {bottomLinks.length > 1 && (
                <button
                  onClick={() =>
                    setBottomLinks((s) => s.filter((_, idx) => idx !== i))
                  }
                  className="text-destructive"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Social Links */}
        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Social Links
            </p>
            <button
              type="button"
              onClick={() =>
                setSocialLinks((s) => [
                  ...s,
                  { platform: "", icon: "", url: "" },
                ])
              }
              className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
              style={{
                background: "hsl(var(--accent-muted))",
                color: "hsl(var(--accent))",
              }}
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          {socialLinks.map((lk, i) => (
            <div
              key={i}
              className="p-3 rounded-lg border border-border space-y-3 mb-2"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">
                  Social {i + 1}
                </p>
                <div className="flex items-center gap-2">
                  <ReorderButtons
                    index={i}
                    total={socialLinks.length}
                    onMove={(f, t) =>
                      setSocialLinks(moveItem(socialLinks, f, t))
                    }
                  />
                  {socialLinks.length > 1 && (
                    <button
                      onClick={() =>
                        setSocialLinks((s) => s.filter((_, idx) => idx !== i))
                      }
                      className="text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Platform">
                  <TextInput
                    value={lk.platform}
                    onChange={(e) =>
                      updateSocialLink(i, "platform", e.target.value)
                    }
                    placeholder="e.g. LinkedIn"
                  />
                </Field>
                <Field label="Icon">
                  <select
                    value={lk.icon}
                    onChange={(e) =>
                      updateSocialLink(i, "icon", e.target.value)
                    }
                    className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground outline-none"
                  >
                    <option value="">Select icon</option>
                    {SOCIAL_ICON_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="URL">
                <TextInput
                  value={lk.url}
                  onChange={(e) => updateSocialLink(i, "url", e.target.value)}
                  placeholder="https://linkedin.com/company/..."
                />
              </Field>
            </div>
          ))}
        </div>
      </CollapsibleSection>
    </div>
  );
}
